import { Component, Input, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { StateService } from '@uirouter/angular';
import { ForumService } from '../forum.service';
import { UiService } from '../../../core/services/core.ui.service';
import { GfTitleService } from '../../../core/services/core.title.service';
import { UserService } from '../../../core/services/core.user.service';
import { GfTranslateService } from '../../../core/services/core.translate.service';
import { UploadService } from '../../../core/services/core.upload.service';
import { ForumThreadDeleteModalComponent } from './deleteThreadModal/delete.thread.modal.component';
import { ThreadEditModalComponent } from '../thread-edit/forum.thread-edit-modal.component';
import { decodeHtmlEntities } from '../../../core/pipes/text/text.functions';
import * as _ from 'lodash';

@Component({
  selector: 'forum-reader-view',
  template: require('./forum.reader.component.html')
})
export class ForumReaderViewComponent implements OnInit {
  @Input() project: any;
  @Input() forum: any;
  @Input() siteConfig: any;
  @Input() projectMenu: any;

  domain: string;
  section: string;
  discussion: any;
  threads: any[];
  currentThread: any;
  currentUser: any;
  showPost: boolean = false;
  showPostBottom: boolean = false;
  permlevel: any;
  canPost: boolean;
  isChat: boolean;
  filter: string = '';
  filteredThreads: any[] = [];
  newMessage: any = { message: '' };
  filesUploading: any[] = [];
  messages: any[] = [];
  uploadFiles: any[] = [];
  loading: boolean = false;

  constructor(
    private stateService: StateService, 
    private forumService: ForumService,
    private uiService: UiService,
    private modalService: NgbModal,
    private userService: UserService,
    private gfTranslateService: GfTranslateService,
    private uploadService: UploadService,
    private titleService: GfTitleService
  ) {
  }

  ngOnInit() {
    this.domain = this.siteConfig.hostName;
    this.section = 'forum';
    this.discussion = this.forum;
    this.threads = this.forum.forumThreads;
    this.filteredThreads = this.threads;

    this.currentThread = this.getThread();
    const threadName = _.get(this.currentThread, 'threadName', '');
    this.titleService.setTitle(this.project.name, 'Discussions', threadName || this.forum.forumName);
    this.currentUser = this.userService.session.getUser();

    this.permlevel = this.project.rel.permissions.forums[this.discussion.id];
    this.canPost = this.permlevel > 1;
    this.isChat = this.forum.forumName === this.project.name;

    this.loadPage();
  }

  select(id: number) {
    this.stateService.go('.', { ...this.stateService.params, thread: id, page_num: 1 })
      .then(() => {
        this.currentThread = this.getThread();
        const threadName = _.get(this.currentThread, 'threadName', '');
        this.titleService.setTitle(this.project.name, 'Discussions', threadName || this.forum.forumName);
        this.loadPage();
      });
  }

  cancelPost() {
    this.showPost = false;
    this.showPostBottom = false;
    this.newMessage.message = '';
  }

  onNewMessageUpdate(message: string) {
    this.newMessage.message = message;
  }

  postNewMessage() {
    this.updateMessage(0, this.newMessage.message, this.currentThread)
    .then(() => {
      this.loadPage();
    });
  }

  updateMessage(id: number, body: string, thread: any, parentId?: number): Promise<any> {
    return new Promise((resolve, reject) => {
      if (body) {
        const threadId = typeof thread === 'object' ? thread.id : thread;
        const payload: any = {
          body: body,
          subject: 'Discussion Message',
          forumThread: threadId
        };
        if (parentId) {
          payload['parentForumMessageId'] = parentId;
        }
        this.forumService.postMessage(id, payload).then(
          (response) => {
            this.uiService.message.showSuccess('Message posted');
            if (id === 0) {
              this.resetPost();
              this.currentThread.messageCount++;
            }

            if (this.filesUploading?.length > 0) {
              const messageId = _.get(response, 'location', '').split('/').pop();
              const options = {
                successCallback: this.finishUpload.bind(this),
                progressCallback: this.progressCallback.bind(this),
                finishedCallback: this.removeFileFromInProgress.bind(this)
              };

              const files = this.filesUploading.map((file) => file.file);
              
              this.uploadService.uploadFiles('forummessage', messageId, files, options)
              .then(() => {
                  resolve(response);
                },
                () => {
                  reject();
                }
              );
              this.filesUploading = [];
            } else {
              resolve(response);
            }
          },
          (response) => {
            this.uiService.message.showError(
              'An error occurred while posting the message: ' + response.statusText
            );
            reject(response);
          }
        );
      } else {
        this.uiService.message.showError('The message cannot be blank');
        reject();
      }
    });
  }

  deleteMessage(id: number) {
    this.forumService.deleteMessage(id).then(
      () => {
        this.loadPage();
      },
      () => {
        this.loadPage();
      }
    );
  }

  setPaging(paging: any) {
    const params = {
      page_num: paging.page_num,
      page_size: paging.page_size,
      sort_dir: paging.sort_dir
    };

    this.stateService.go('.', params, {notify: true, reload: true})
    .then(() => this.loadPage());
  }

  togglePostBottom() {
    this.showPostBottom = !this.showPostBottom;
  }

  togglePostTop() {
    this.showPost = !this.showPost;
  }

  editThread(thread: any) {
    const modalRef = this.modalService.open(ThreadEditModalComponent);
    modalRef.componentInstance.discussion = this.discussion;
    modalRef.componentInstance.thread = thread;
    modalRef.componentInstance.postThread = this.postThread.bind(this);
    modalRef.componentInstance.onClose.subscribe(() => {
	    modalRef.close();
    });
  }

  private resetPost() {
    this.showPost = false;
    this.showPostBottom = false;
    this.newMessage.message = '';
  }

  private updateThreads() {
    this.forumService.forumSummary(this.discussion.id).then(
      (data: any) => {
        this.discussion = data;
        this.threads = data.forumThreads;
        this.filteredThreads = this.threads;
        if (this.threads.length > 0) {
          this.select(0);
        }
      },
      (response) => {
        // Handle error
      }
    );
  }

  private getThread() {
    const threadId = this.stateService.params.thread;
    return _.find(this.threads, (item) => item.id == threadId) || _.first(this.threads);
  }

  private loadPage() {
    const id = _.get(this.currentThread, 'id', false);
    if (!id) {
      this.messages = [];
      return;
    }

    this.loading = true;
    // Hide the "Post Message" editor during loading
    this.showPost = false;
    this.showPostBottom = false;

    const params = {
      page_num: this.stateService.params.page_num,
      page_size: this.stateService.params.page_size,
      sort_dir: this.stateService.params.sort_dir,
      sort_field: this.stateService.params.sort_field
    };
    
    this.forumService.threadMessages(id, 0, params).then(
      (response) => {
        this.messages = _.map(_.get(response, 'items', []), (item) => {
          item.body = _.unescape(decodeHtmlEntities(item.body));
          if (item.rel && item.rel.attachments) {
            if (!Array.isArray(item.rel.attachments)) {
              item.rel.attachments = Object.values(item.rel.attachments);
            }
          } else {
            item.rel.attachments = [];
          }
          return item;
        });
        this.loading = false;
      },
      (response) => {
        console.log(response);
        this.messages = [];
        this.loading = false;
      }
    );
  }

  private postThread(thread: any) {
    const threadId = thread.id;

    this.forumService.postThread(this.discussion.id, threadId, thread).then(
      (response) => {
        if (threadId === 0) {
          this.uiService.message.showSuccess('Thread created');
        } else {
          this.uiService.message.showSuccess('Thread updated');
        }
        if (threadId > 0) {
          this.loadPage();
        } else {
          this.updateThreads();
        }
      },
      (response) => {
        if (threadId === 0) {
          this.uiService.message.showError(
            'An error occurred while creating the thread: ' + response.statusText
          );
        } else {
          this.uiService.message.showError(
            'An error occurred while saving the thread: ' + response.statusText
          );
        }
      }
    );
  }

  progressCallback(
    section: string,
    refId: number,
    file: any,
    bytesUploaded: number,
    bytesRemaining: number,
    percentUploaded: number,
    percentRemaining: number
  ) {
    const fileInProgress = _.find(this.filesUploading, { name: file.name });

    if (!_.isEmpty(fileInProgress)) {
      fileInProgress.amountComplete = bytesUploaded;
      fileInProgress.amountTotal = bytesRemaining;
      fileInProgress.percentComplete = percentUploaded;
      fileInProgress.percentRemaining = percentRemaining;
    }
  }

  removeFileFromInProgress(section: string, refId: number, file: any) {
    _.remove(this.filesUploading, (_file: any) => _file.name === file.name);
  }

  fileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      for (var i = 0; i < input.files.length; i++) {
        var file = input.files[i];
        this.uploadFiles.unshift(file);
      }
      this.addFilesToUploading(input.files);
      input.value = '';
    }
  }

  private addFilesToUploading(files: any) {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      this.filesUploading.push({
        name: file.name,
        size: file.size,
        bytesUploaded: 0,
        bytesRemaining: file.size,
        percentUploaded: 0,
        percentRemaining: 100,
        file: file
      });
    }
  }

  removeAttachment(id: number) {
    const attachment = this.uploadFiles[id];
    let fileName = '';
    if (!_.isEmpty(attachment)) {
      fileName = attachment.name;
    }
    this.uploadFiles.splice(id, 1);
    this.filesUploading.splice(id, 1);
    const msg = this.gfTranslateService.translate(' Attachment {{fileName}} has been removed', {
      fileName: fileName
    });
    this.uiService.message.showSuccess(msg, 'Attachment Remove');
  }

  resetFiles() {
    this.uploadFiles = [];
  }

  private finishUpload(section: string, refId: number, file: any, hash: string) {
    const params = {
      fileName: file.name,
      fileType: file.type ? file.type : 'application/octet-stream'
    };

    return this.uploadService.finishUpload(section, refId, file.name, hash).then((response) => {
      console.log(response);
    });
  }

  public goBack() {
    this.stateService.go('home.project.summary.forum.browse', this.stateService.params);
  }

  public showThreadDeleteModal() {
    if (!this.canPost) return; 

    const modalRef = this.modalService.open(ForumThreadDeleteModalComponent);
    modalRef.componentInstance.thread = this.currentThread;
    modalRef.componentInstance.onClose.subscribe(() => {
	    modalRef.close();
    });
  }

  onFilterChange(newValue: string) {
    if (!this.threads) return;
    
    if (!newValue) {
      this.filteredThreads = this.threads;
    } else {
      const searchTerm = newValue.toLowerCase();
      this.filteredThreads = this.threads.filter(thread => 
        thread.threadName.toLowerCase().includes(searchTerm)
      );
    }
  }
}